import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import NeoInput from "./NeoInput";

/**
 * NeoDateRangePicker
 * Componente premium de seleção de período (Start e End date) no mesmo painel de calendário.
 * - Input base com ícone que mostra o intervalo selecionado (ex: 20/05/2026 — 25/05/2026).
 * - Popup de calendário integrado com realce visual da faixa de datas selecionada (Range)
 *   e efeito táctil ao passar o mouse (Hover Range).
 * - Totalmente compatível com o tema Taça Outono premium.
 */
const NeoDateRangePicker = ({
    startDate = "", // padrão ISO 'YYYY-MM-DD'
    endDate = "",   // padrão ISO 'YYYY-MM-DD'
    onChange,       // callback({ startDate, endDate }) com strings ISO
    placeholder = "Selecione o período...",
    disabled = false,
    label = "",
    size = "md",
    className = "",
    ...props
}) => {
    const wrapperRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [hoveredDate, setHoveredDate] = useState(null);

    // Mapeamento de nomes de meses em português
    const MONTH_NAMES = [
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];

    // Conversores seguros de string ISO para Objetos Date locais (sem desvio de fuso)
    const parseISO = (str) => {
        if (!str) return null;
        const parts = str.split("-");
        if (parts.length !== 3) return null;
        return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 0, 0, 0, 0);
    };

    const formatISO = (date) => {
        if (!date) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const formatDateBR = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length !== 3) return "";
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    };

    const start = useMemo(() => parseISO(startDate), [startDate]);
    const end = useMemo(() => parseISO(endDate), [endDate]);

    // Sincroniza o calendário com a data selecionada inicialmente
    useEffect(() => {
        if (start) {
            setCurrentDate(new Date(start));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate]);

    // Fechar ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Texto de exibição no input (ex: "25/05/2026 — 29/05/2026")
    const displayValue = useMemo(() => {
        if (startDate && endDate) {
            return `${formatDateBR(startDate)} — ${formatDateBR(endDate)}`;
        }
        if (startDate) {
            return `${formatDateBR(startDate)} — Selecione o fim...`;
        }
        return "";
    }, [startDate, endDate]);

    // Cálculos de dias do calendário
    const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
    const getFirstDayOfMonth = (y, m) => new Date(y, m, 1).getDay();

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const totalDays = getDaysInMonth(year, month);
    const firstDayIndex = getFirstDayOfMonth(year, month);

    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);

    const days = [];

    // Dias vizinhos do mês anterior
    for (let i = firstDayIndex - 1; i >= 0; i--) {
        days.push({
            day: daysInPrevMonth - i,
            month: prevMonth,
            year: prevYear,
            isNeighbor: true
        });
    }

    // Dias do mês atual
    for (let i = 1; i <= totalDays; i++) {
        days.push({
            day: i,
            month,
            year,
            isNeighbor: false
        });
    }

    // Dias vizinhos do próximo mês
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
        days.push({
            day: i,
            month: nextMonth,
            year: nextYear,
            isNeighbor: true
        });
    }

    // Ações do calendário
    const changeMonth = (offset) => {
        setCurrentDate(new Date(year, month + offset, 1));
    };

    const changeYear = (offset) => {
        setCurrentDate(new Date(year + offset, month, 1));
    };

    const handleDayClick = (dayObj) => {
        if (disabled) return;
        const clickedDate = new Date(dayObj.year, dayObj.month, dayObj.day, 0, 0, 0, 0);

        if (!start || (start && end)) {
            // Primeira data selecionada (reseta o range e vira data inicial)
            onChange({ startDate: formatISO(clickedDate), endDate: "" });
        } else {
            // Segunda data selecionada
            if (clickedDate < start) {
                // Se clicar em data anterior ao início, ela passa a ser a nova data de início
                onChange({ startDate: formatISO(clickedDate), endDate: "" });
            } else {
                // Completa o intervalo
                onChange({ startDate: startDate, endDate: formatISO(clickedDate) });
                setIsOpen(false); // Fecha o calendário automaticamente ao fechar o range
            }
        }
    };

    const handleDayMouseEnter = (dayObj) => {
        if (start && !end) {
            const dateObj = new Date(dayObj.year, dayObj.month, dayObj.day, 0, 0, 0, 0);
            if (dateObj >= start) {
                setHoveredDate(dateObj);
            } else {
                setHoveredDate(null);
            }
        }
    };

    // Verificadores de estados visuais para cada dia no grid
    const getDayState = (dayObj) => {
        const dateObj = new Date(dayObj.year, dayObj.month, dayObj.day, 0, 0, 0, 0);
        
        const isStart = start && start.getTime() === dateObj.getTime();
        const isEnd = end && end.getTime() === dateObj.getTime();
        
        // Em intervalo selecionado sólido
        const isInRange = start && end && dateObj > start && dateObj < end;
        
        // Em intervalo pré-selecionado ao passar o mouse
        const isInHoverRange = start && !end && hoveredDate && dateObj > start && dateObj <= hoveredDate;

        return { isStart, isEnd, isInRange, isInHoverRange };
    };

    const isToday = (dayObj) => {
        const today = new Date();
        return today.getDate() === dayObj.day &&
               today.getMonth() === dayObj.month &&
               today.getFullYear() === dayObj.year;
    };

    return (
        <div ref={wrapperRef} className={`neo-datepicker-wrapper ${className}`} style={{ position: "relative" }}>
            {/* Input Gatilho (Reutiliza os estilos de NeoInput com ícone de range) */}
            <NeoInput
                label={label}
                placeholder={placeholder}
                value={displayValue}
                onFocus={() => !disabled && setIsOpen(true)}
                readOnly={true}
                disabled={disabled}
                size={size}
                icon="mdi-calendar-range"
                style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                inputStyle={{ cursor: disabled ? "not-allowed" : "pointer" }}
                {...props}
            />

            {/* Painel do Calendário Flutuante de Range */}
            {isOpen && !disabled && (
                <div className="neo-datepicker-calendar" style={{ minWidth: "290px" }}>
                    {/* Cabeçalho de Navegação */}
                    <div className="neo-datepicker-header">
                        <div className="d-flex gap-1">
                            <button type="button" className="neo-datepicker-header-btn" onClick={() => changeYear(-1)}>
                                <i className="mdi mdi-chevron-double-left font-size-14"></i>
                            </button>
                            <button type="button" className="neo-datepicker-header-btn" onClick={() => changeMonth(-1)}>
                                <i className="mdi mdi-chevron-left font-size-14"></i>
                            </button>
                        </div>

                        <h6 className="neo-datepicker-month-title">
                            {MONTH_NAMES[month]} {year}
                        </h6>

                        <div className="d-flex gap-1">
                            <button type="button" className="neo-datepicker-header-btn" onClick={() => changeMonth(1)}>
                                <i className="mdi mdi-chevron-right font-size-14"></i>
                            </button>
                            <button type="button" className="neo-datepicker-header-btn" onClick={() => changeYear(1)}>
                                <i className="mdi mdi-chevron-double-right font-size-14"></i>
                            </button>
                        </div>
                    </div>

                    {/* Dias da Semana */}
                    <div className="neo-datepicker-weekdays">
                        <span className="neo-datepicker-weekday">D</span>
                        <span className="neo-datepicker-weekday">S</span>
                        <span className="neo-datepicker-weekday">T</span>
                        <span className="neo-datepicker-weekday">Q</span>
                        <span className="neo-datepicker-weekday">Q</span>
                        <span className="neo-datepicker-weekday">S</span>
                        <span className="neo-datepicker-weekday">S</span>
                    </div>

                    {/* Grade de Dias com Ranges */}
                    <div className="neo-datepicker-days-grid" onMouseLeave={() => setHoveredDate(null)}>
                        {days.map((dayObj, idx) => {
                            const { isStart, isEnd, isInRange, isInHoverRange } = getDayState(dayObj);
                            const isTdy = isToday(dayObj);

                            const cellClass = [
                                "neo-datepicker-day",
                                dayObj.isNeighbor ? "neo-datepicker-day-neighbor" : "",
                                isStart ? "neo-datepicker-day-selected neo-datepicker-day-range-start" : "",
                                isEnd ? "neo-datepicker-day-selected neo-datepicker-day-range-end" : "",
                                isInRange ? "neo-datepicker-day-in-range" : "",
                                isInHoverRange ? "neo-datepicker-day-in-hover-range" : "",
                                isTdy ? "neo-datepicker-day-today" : ""
                            ].filter(Boolean).join(" ");

                            return (
                                <div
                                    key={idx}
                                    className={cellClass}
                                    onClick={() => handleDayClick(dayObj)}
                                    onMouseEnter={() => handleDayMouseEnter(dayObj)}
                                    style={{ cursor: "pointer" }}
                                >
                                    {dayObj.day}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

NeoDateRangePicker.propTypes = {
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    className: PropTypes.string
};

export default NeoDateRangePicker;
